import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import { erpIntegrationService } from '../../../services/erpIntegrationService';

const SyncSchedulingPanel = ({ connection }) => {
  const [schedules, setSchedules] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (connection?.id) {
      loadSchedules();
      loadSyncLogs();
    }
  }, [connection]);

  const loadSchedules = async () => {
    try {
      const data = await erpIntegrationService?.getSyncSchedules(connection?.id);
      setSchedules(data);
    } catch (error) {
      console.error('Error loading sync schedules:', error);
    }
  };

  const loadSyncLogs = async () => {
    try {
      setLoading(true);
      const data = await erpIntegrationService?.getSyncLogs(connection?.id, 10);
      setSyncLogs(data);
    } catch (error) {
      console.error('Error loading sync logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSyncStatusBadge = (status) => {
    const statusConfig = {
      completed: { label: 'Completed', color: 'bg-success/10 text-success', icon: 'CheckCircle' },
      failed: { label: 'Failed', color: 'bg-error/10 text-error', icon: 'XCircle' },
      in_progress: { label: 'In Progress', color: 'bg-warning/10 text-warning', icon: 'Loader' },
      partial: { label: 'Partial', color: 'bg-warning/10 text-warning', icon: 'AlertCircle' }
    };
    const config = statusConfig?.[status] || statusConfig?.completed;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground mb-1">Sync Scheduling</h2>
        <p className="text-sm text-muted-foreground">
          Automated sync schedules for {erpIntegrationService?.getERPSystemLabel(connection?.erpSystem)}
        </p>
      </div>

      {/* Active Schedules */}
      <div className="bg-background border border-border rounded-lg p-5 mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Active Schedules</h3>
        <div className="space-y-3">
          {schedules?.map((schedule) => (
            <div key={schedule?.id} className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
              <div className="flex-1">
                <h4 className="font-medium text-foreground mb-1">{schedule?.scheduleName}</h4>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    {schedule?.syncFrequency}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="ArrowLeftRight" size={14} />
                    {schedule?.syncDirection?.replace('_', ' ')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Database" size={14} />
                    {schedule?.entityTypes?.length} entities
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors text-sm">
                  Run Now
                </button>
                <button className="px-3 py-2 border border-border rounded hover:bg-muted transition-colors">
                  <Icon name="Settings" size={16} className="text-muted-foreground" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Sync Logs */}
      <div className="bg-background border border-border rounded-lg p-5">
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Sync Activity</h3>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading sync logs...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Entity</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Direction</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Records</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Duration</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Started</th>
                </tr>
              </thead>
              <tbody>
                {syncLogs?.map((log) => (
                  <tr key={log?.id} className="border-b border-border last:border-0">
                    <td className="py-3 px-4">
                      <span className="text-sm font-medium text-foreground capitalize">
                        {log?.entityType?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-muted-foreground capitalize">
                        {log?.syncDirection?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {getSyncStatusBadge(log?.syncStatus)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm text-foreground">
                        {log?.successfulRecords}/{log?.totalRecords}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm text-muted-foreground">
                        {log?.durationSeconds}s
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-muted-foreground">
                        {new Date(log?.startedAt)?.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncSchedulingPanel;
